import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIconTypeMap,
  Typography,
  useTheme,
} from "@mui/material";
import { useRecordContext } from "react-admin";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { TheoryLesson } from "../../model/autovioCalendarEvents";
import { EventDialogHeader } from "../EventDialog";
import { ShortEventHistory } from "./ShortEventHistory";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { autovioColors, menuItemStyle, menuPaperStyle, statusMenuIconStyle } from "../../misc/backofficeTheme";
import { Student } from "../../providers/studentsProvider";
import { TheoryLessonDetails } from "./EventInfoCard";
import { getTheoryLessonAttendanceState, TheoryLessonAttendanceState } from "../../model/autovioCalendarEvents";
import { useMenu } from "../../hooks/useMenu";
import { serverAPI } from "../../api/server.api";
import { LoadingIndicator } from "../../misc/LoadingIndicator";

export function StudentTheoryLessonDialog(props: { event: TheoryLesson; onClose: () => void }) {
  const { event, onClose } = props;
  const student = useRecordContext<Student>();
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" } } }}
    >
      <DialogTitle sx={{ pl: { xs: 2, sm: "30px" }, pr: { xs: 7, sm: "30px" } }}>
        <EventDialogHeader event={event} mode="view" closeDialog={onClose} />
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent sx={{ px: { xs: 2, sm: "30px" } }}>
        <Box sx={{ "& .MuiTypography-root": { fontSize: "14px", overflowWrap: "anywhere" } }}>
          <ShortEventHistory event={event} studentContext={student} />
        </Box>
        <Column
          spacing="0.5em"
          data-testid="StudentTheoryLesson-details"
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
            color: "text.secondary",
            "& a": { color: "inherit" },
            "& .MuiTypography-root": { whiteSpace: "normal", overflowWrap: "anywhere" },
          }}
        >
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            Details
          </Typography>
          <TheoryLessonDetails event={event} />
          <_TheoryLessonAttendance event={event} />
        </Column>
      </DialogContent>
    </Dialog>
  );
}

const _TheoryLessonAttendance = ({ event }: { event: TheoryLesson }) => {
  const theme = useTheme();
  const student = useRecordContext<Student>();
  const studentId = student.id;
  const renderAttendanceStatus = () => {
    let status: {
      icon: { element: OverridableComponent<SvgIconTypeMap>; fill: string; backgroundColor: string };
      action: string;
    };
    switch (getTheoryLessonAttendanceState(studentId, event)) {
      case "attended":
        status = {
          icon: { element: CheckIcon, fill: theme.palette.common.white, backgroundColor: autovioColors.green },
          action: "teilgenommen",
        };
        break;
      case "no-show":
        status = {
          icon: { element: CloseIcon, fill: theme.palette.common.white, backgroundColor: autovioColors.red },
          action: "nicht teilgenommen",
        };
        break;
      case "cancelled":
        status = {
          icon: { element: CloseIcon, fill: theme.palette.common.white, backgroundColor: autovioColors.red },
          action: "abgesagt",
        };
        break;
      case "booked":
        status = {
          icon: {
            element: HourglassEmptyIcon,
            fill: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
          },
          action: "gebucht",
        };
        break;
    }

    const Icon = status.icon.element;
    return (
      <>
        <Icon
          sx={{
            fill: status.icon.fill,
            backgroundColor: status.icon.backgroundColor,
            borderRadius: "50%",
            width: "12px",
            height: "12px",
            padding: "2px",
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
          {student.name} hat {status.action} <UpdateStudentAttendanceMenu event={event} studentId={studentId} />
        </Typography>
      </>
    );
  };
  return (
    <Row spacing={1} sx={{ alignItems: "center", pl: "2px", minWidth: 0 }}>
      {renderAttendanceStatus()}
    </Row>
  );
};

function UpdateStudentAttendanceMenu({ event, studentId }: { event: TheoryLesson; studentId: string }) {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSelect = (action: TheoryLessonAttendanceState) => async () => {
    setIsUpdating(true);
    await runSafely(
      () =>
        serverAPI.updateTheoryLessonAttendance({
          eventId: event.id,
          attendance: { [studentId]: action },
        }),
      {
        errorMessage: "Fehler beim Speichern der Anwesenheit.",
        successMessage: "Anwesenheit erfolgreich aktualisiert.",
        onSuccess: () => {
          closeMenu();
          // Delay loading removal to avoid flickering effect until menu is closed ...
          setTimeout(() => setIsUpdating(false), 200);
        },
      },
    );
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={openMenu}
        data-testid="editAttendanceButton"
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{ verticalAlign: "baseline" }}
      >
        Status ändern
      </Link>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: menuPaperStyle } }}
      >
        {isUpdating ? (
          <Box sx={{ height: "96px", width: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LoadingIndicator />
          </Box>
        ) : (
          _actions
            .filter(({ state }) => state !== getTheoryLessonAttendanceState(studentId, event))
            .map(({ state, label, icon }) => (
              <MenuItem key={state} onClick={handleSelect(state)} sx={menuItemStyle}>
                <ListItemIcon>
                  {icon === "check" ? (
                    <CheckIcon sx={statusMenuIconStyle(icon)} />
                  ) : (
                    <CloseIcon sx={statusMenuIconStyle(icon)} />
                  )}
                </ListItemIcon>
                <ListItemText>{label}</ListItemText>
              </MenuItem>
            ))
        )}
      </Menu>
    </>
  );
}

const _actions = [
  { label: "gebucht", state: "booked", icon: "check" },
  { label: "teilgenommen", state: "attended", icon: "check" },
  { label: "abgesagt", state: "cancelled", icon: "close" },
  { label: "nicht erschienen", state: "no-show", icon: "close" },
] as const;
